import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, ArrowRight } from "lucide-react";
import { FAQS } from "@/data/index";
import { springPresets } from "@/lib/motion";

/* @section: faq-hero */
function FAQHero() {
  return (
    <section className="py-28 bg-background border-b border-border">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={springPresets.gentle}
          className="max-w-2xl"
        >
          <span className="text-xs text-primary tracking-[0.3em] uppercase mb-6 block">FAQ</span>
          <h1
            className="text-4xl md:text-5xl font-semibold text-foreground leading-[1.3] mb-6"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            よくある質問
          </h1>
          <div className="w-12 h-px bg-primary mb-6" />
          <p className="text-muted-foreground text-lg leading-[1.9]">
            研修・コーチングに関するよくあるご質問をまとめました。
            ご不明な点はお気軽にお問い合わせください。
          </p>
        </motion.div>
      </div>
    </section>
  );
}

/* @section: faq-list */
function FAQList() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const categories = Array.from(new Set(FAQS.map((f) => f.category)));

  return (
    <section className="py-24 bg-background">
      <div className="container mx-auto px-6 max-w-3xl">
        {categories.map((cat) => (
          <div key={cat} className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-5 bg-primary rounded-full" />
              <h2
                className="text-lg font-semibold text-foreground"
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                {cat}
              </h2>
            </div>
            <div className="flex flex-col gap-2">
              {FAQS.filter((f) => f.category === cat).map((faq, i) => {
                const globalIndex = FAQS.indexOf(faq);
                const isOpen = openIndex === globalIndex;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 8 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ ...springPresets.gentle, delay: i * 0.05 }}
                    className="border border-border rounded-sm overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenIndex(isOpen ? null : globalIndex)}
                      className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-muted/30 transition-colors"
                    >
                      <span className="text-sm font-medium text-foreground leading-relaxed pr-4">
                        {faq.q}
                      </span>
                      <span className="shrink-0 text-primary">
                        {isOpen ? <Minus size={16} /> : <Plus size={16} />}
                      </span>
                    </button>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.22, ease: "easeInOut" }}
                        >
                          <div className="px-6 pb-6 pt-1 border-t border-border bg-muted/20">
                            <p className="text-sm text-muted-foreground leading-[1.9]">{faq.a}</p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* @section: faq-cta */
function FAQCTA() {
  return (
    <section className="py-20 bg-muted/30 border-t border-border">
      <div className="container mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={springPresets.gentle}
        >
          <h2
            className="text-2xl font-semibold text-foreground mb-3"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            解決しない疑問はお気軽にどうぞ。
          </h2>
          <p className="text-muted-foreground mb-8">初回のご相談は無料です。</p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground text-sm tracking-wider hover:opacity-90 transition-opacity rounded-sm"
            style={{ letterSpacing: "0.1em" }}
          >
            お問い合わせはこちら
            <ArrowRight size={15} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

export default function FAQ() {
  return (
    <>
      <FAQHero />
      <FAQList />
      <FAQCTA />
    </>
  );
}
